/* eslint-disable @typescript-eslint/member-ordering */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { UserService } from 'src/app/shared/user.service';
import { APIService } from 'src/app/shared/api.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthService {
  private _authenticated: boolean = false;
  private _isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );

  /**
   * Constructor
   */
  constructor(
    private _httpClient: HttpClient,
    private _userService: UserService,
    private _apiService: APIService,
    private _router: Router
  ) {
    if (this.accessToken) {
      this._isAuthenticated.next(true);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for files
   */
  get isAuthenticated$(): Observable<boolean> {
    return this._isAuthenticated.asObservable();
  }

  /**
   * Setter & getter for access token
   */
  get accessToken(): string {
    // return localStorage.getItem('accessToken') ?? '';
    if (localStorage.getItem('accessToken')) {
      return localStorage.getItem('accessToken');
    } else {
      return '';
    }
  }

  set accessToken(token: string) {
    localStorage.setItem('accessToken', token);
  }

  /**
   * Setter & getter for refresh token
   */
  get refreshToken(): string {
    if (localStorage.getItem('refreshToken')) {
      return localStorage.getItem('refreshToken');
    } else {
      return '';
    }
  }

  set refreshToken(token: string) {
    localStorage.setItem('refreshToken', token);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Forgot password
   *
   * @param email
   */
  forgotPassword(email: string): Observable<any> {
    return this._httpClient.post('api/auth/forgot-password', email);
  }

  /**
   * Reset password
   *
   * @param password
   */
  resetPassword(password: string): Observable<any> {
    return this._httpClient.post('api/auth/reset-password', password);
  }

  /**
   * Sign in
   *
   * @param credentials
   */
  signIn(credentials: {
    nationalCode: string;
    password: string;
  }): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      const err = new Error('User is already logged in.');
      return throwError(() => err);
    }

    return this._apiService.post('auth/login', credentials).pipe(
      switchMap((response: any) => {
        // Store the access tokens in the local storage
        this.accessToken = response.accessToken;
        this.refreshToken = response.refreshToken;

        // Set the authenticated flag to true
        this._authenticated = true;
        this._isAuthenticated.next(true);
        // Store the user on the user service
        this._userService.user = response.data;

        // Return a new observable with the response
        return of(response);
      })
    );
  }

  /**
   * Sign in using the access token
   */
  signInUsingToken(): Observable<any> {
    // Sign in using the token
    return this._httpClient
      .post('api/auth/sign-in-with-token', {
        accessToken: this.accessToken,
      })
      .pipe(
        catchError(() =>
          // Return false
          of(false)
        ),
        switchMap((response: any) => {
          // Replace the access token with the new one if it's available on
          // the response object.
          //
          // This is an added optional step for better security. Once you sign
          // in using the token, you should generate a new one on the server
          // side and attach it to the response object. Then the following
          // piece of code can replace the token with the refreshed one.
          if (response.accessToken) {
            this.accessToken = response.accessToken;
          }

          // Set the authenticated flag to true
          this._authenticated = true;
          this._isAuthenticated.next(true);
          // Store the user on the user service
          this._userService.user = response.user;

          // Return true
          return of(true);
        })
      );
  }

  /**
   * Sign out
   */
  signOut(): Observable<any> {
    // Remove the access token from the local storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('device');

    // Set the authenticated flag to false
    this._authenticated = false;
    this._isAuthenticated.next(false);
    this._router.navigateByUrl('/login');
    // Return the observable
    return of(true);
  }

  /**
   * Sign up
   *
   * @param newUser
   */
  signUp(newUser: { mobile: string }): Observable<any> {
    return this._apiService.post('auth/register', newUser);
  }

  /**
   * Verify mobile
   *
   * @param entity
   */
  verifyMobile(entity: { mobile: string; code: string }): Observable<any> {
    return this._apiService.post('auth/verify-mobile', entity).pipe(
      switchMap((response: any) => {
        // Store the access tokens in the local storage
        this.accessToken = response.accessToken;
        this.refreshToken = response.refreshToken;

        // Set the authenticated flag to true
        this._authenticated = true;

        // Store the user on the user service
        this._userService.user = response.data;

        // Return a new observable with the response
        return of(response);
      })
    );
  }

  /**
   * Unlock session
   *
   * @param credentials
   */
  unlockSession(credentials: {
    email: string;
    password: string;
  }): Observable<any> {
    return this._httpClient.post('api/auth/unlock-session', credentials);
  }

  /**
   * Check the authentication status
   */
  check(): Observable<boolean> {
    // Check if the user is logged in
    if (this._authenticated) {
      return of(true);
    }

    // Check the access token availability
    if (!this.accessToken) {
      return of(false);
    }

    // Check the access token expire date
    // if (AuthUtils.isTokenExpired(this.accessToken)) {
    //   return of(false);
    // }

    // If the access token exists and it didn't expire, sign in using it
    return this.signInUsingToken();
  }
}
